import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  People as PeopleIcon,
  PhoneAndroid as AndroidIcon,
  PhoneIphone as IosIcon,
  Computer as WebIcon,
  AccountCircle,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  ManageAccounts as ManageAccountsIcon,
  Analytics as AnalyticsIcon,
  Message as MessageIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { adminApi } from '../services/api';
import { DashboardStats, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import GrowthChart from './GrowthChart';
import PlatformChart from './PlatformChart';
import UserManagement from './UserManagement';
import PetAnalytics from './PetAnalytics';
import MessagingAnalytics from './MessagingAnalytics';
import UserEngagementAnalyticsComponent from './UserEngagementAnalytics';
import LostPetsWidget from './LostPetsWidget';
import AdoptionHistory from './AdoptionHistory';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  const { logout } = useAuth();

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Use visibility refresh hook for dashboard stats
  const { lastRefresh, manualRefresh } = useVisibilityRefresh(fetchDashboardStats, {
    refreshInterval: 30000, // 30 seconds
    isVisible: activeTab === 0, // Only refresh when dashboard tab is active
    enabled: true,
  });

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleMenuClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case 'ios':
        return <IosIcon color="primary" />;
      case 'android':
        return <AndroidIcon color="success" />;
      case 'web':
        return <WebIcon color="info" />;
      default:
        return <WebIcon color="disabled" />;
    }
  };

  const getPlatformColor = (platform: string): "primary" | "success" | "info" | "default" => {
    switch (platform?.toLowerCase()) {
      case 'ios':
        return 'primary';
      case 'android':
        return 'success';
      case 'web':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert 
          severity="error" 
          sx={{ borderRadius: 3 }}
          action={
            <IconButton color="inherit" size="small" onClick={fetchDashboardStats}>
              <RefreshIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!stats) return null;

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'grey.50', minHeight: '100vh' }}>
      {/* Modern Header */}
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Toolbar sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
            <Box 
              sx={{ 
                width: 48, 
                height: 48, 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3
              }}
            >
              <img 
                src="/patigo_nobg.png" 
                alt="Patigo Logo" 
                style={{ 
                  width: '40px', 
                  height: '40px', 
                  objectFit: 'contain' 
                }} 
              />
            </Box>
            <Box>
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 700 }}>
                Patigo Admin
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                Pet Adoption Analytics Dashboard
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Box textAlign="right">
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Son güncelleme
              </Typography>
              <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                {format(lastRefresh, 'HH:mm:ss')}
              </Typography>
            </Box>
            <IconButton 
              color="inherit" 
              onClick={manualRefresh}
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { borderRadius: 3, mt: 1 }
              }}
            >
              <MenuItem onClick={handleLogout} sx={{ borderRadius: 2, mx: 1 }}>
                Çıkış Yap
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Modern Tab Navigation */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white'
        }}
      >
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              borderRadius: 3,
              mx: 0.5,
              my: 1,
              minHeight: 48,
              fontWeight: 600,
              minWidth: 'auto',
              px: 2,
              '&.Mui-selected': {
                backgroundColor: 'primary.50',
                color: 'primary.main'
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none'
            },
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }
          }}
        >
          <Tab icon={<DashboardIcon />} label="Dashboard" />
          <Tab icon={<AnalyticsIcon />} label="Pet Analitikleri" />
          <Tab icon={<HistoryIcon />} label="Sahiplenme Geçmişi" />
          <Tab icon={<PeopleIcon />} label="Kullanıcı Etkileşimi" />
          <Tab icon={<MessageIcon />} label="Mesajlaşma" />
          <Tab icon={<ManageAccountsIcon />} label="Kullanıcı Yönetimi" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {/* Summary Stats - Single Row */}
          <Box sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            backgroundColor: 'white',
            p: 2.5,
            mb: 4
          }}>
            <Box 
              display="grid" 
              gridTemplateColumns={{ 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(3, 1fr)', 
                lg: 'repeat(4, 1fr)', 
                xl: 'repeat(6, 1fr)' 
              }} 
              gap={3}
              sx={{ alignItems: 'start' }}
            >
              {/* Total Users */}
              <Box sx={{ 
                textAlign: 'center', 
                cursor: 'pointer',
                minHeight: 100,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }} onClick={() => setActiveTab(5)}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                  {stats.totalActiveUsers.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Toplam Kullanıcı
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  +{stats.newUsersThisMonth} bu ay
                </Typography>
              </Box>

              {/* New Users Today */}
              <Box sx={{ 
                textAlign: 'center',
                minHeight: 100,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 0.5 }}>
                  {stats.newUsersToday}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Bugün Yeni
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Bu hafta: {stats.newUsersThisWeek}
                </Typography>
              </Box>

              {/* Active Pets */}
              <Box sx={{ 
                textAlign: 'center', 
                cursor: 'pointer',
                minHeight: 100,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }} onClick={() => setActiveTab(1)}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main', mb: 0.5 }}>
                  {stats.activePets}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Aktif Hayvanlar
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Bekleyen: {stats.pendingPets} | Toplam: {stats.totalPets}
                </Typography>
              </Box>


              {/* Active Users Ever */}
              <Box sx={{ 
                textAlign: 'center',
                minHeight: 100,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main', mb: 0.5 }}>
                  {Math.round((stats.activeUsersEver / stats.totalActiveUsers) * 100)}%
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Aktif Kullanıcı Oranı
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {stats.activeUsersEver?.toLocaleString() || 0} / {stats.totalActiveUsers}
                </Typography>
              </Box>

              {/* Users with Favorites */}
              <Box sx={{ 
                textAlign: 'center',
                minHeight: 100,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main', mb: 0.5 }}>
                  {stats.usersWithFavorites?.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Favori Kullanan
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Engagement oranı
                </Typography>
              </Box>

              {/* Passive Users */}
              <Box sx={{ 
                textAlign: 'center',
                minHeight: 100,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'grey.600', mb: 0.5 }}>
                  {stats.passiveUsers?.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Pasif Kullanıcı
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Kayıt olmuş, girmemiş
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Modern Charts Section */}
          <Box 
            display="grid" 
            gridTemplateColumns={{ 
              xs: '1fr', 
              sm: '1fr', 
              md: '2fr 1fr',
              lg: '2fr 1fr'
            }} 
            gap={3} 
            sx={{ mb: 4 }}
          >
            <GrowthChart isVisible={activeTab === 0} />
            <PlatformChart isVisible={activeTab === 0} />
          </Box>

          {/* Lost Pets Widget */}
          <Box 
            display="grid" 
            gridTemplateColumns={{ 
              xs: '1fr', 
              sm: '1fr', 
              md: '1fr',
              lg: '1fr'
            }} 
            gap={3} 
            sx={{ mb: 4 }}
          >
            <LostPetsWidget isVisible={activeTab === 0} />
          </Box>

          {/* Modern Recent Users Table */}
          <Card elevation={0} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Son Kayıt Olan Kullanıcılar
              </Typography>
              <TableContainer 
                component={Paper} 
                elevation={0}
                sx={{ 
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>İsim</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Kayıt Tarihi</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Son Giriş</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.recentUsers.map((user: User) => (
                      <TableRow 
                        key={user._id}
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: 'grey.50' 
                          },
                          '&:last-child td': { 
                            borderBottom: 0 
                          }
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar 
                              sx={{ 
                                mr: 2, 
                                width: 36, 
                                height: 36,
                                backgroundColor: 'primary.main',
                                fontWeight: 600
                              }}
                            >
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {user.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={getPlatformIcon(user.platform)}
                            label={user.platform || 'Bilinmeyen'}
                            color={getPlatformColor(user.platform)}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(user.createdAt), 'dd.MM.yyyy HH:mm')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {user.lastLoginAt 
                              ? format(new Date(user.lastLoginAt), 'dd.MM.yyyy HH:mm')
                              : 'Hiç giriş yapmadı'
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <PetAnalytics isVisible={activeTab === 1} />
        </Box>
      )}

      {activeTab === 2 && (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <AdoptionHistory />
        </Box>
      )}

      {activeTab === 3 && (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <UserEngagementAnalyticsComponent isVisible={activeTab === 3} />
        </Box>
      )}

      {activeTab === 4 && (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <MessagingAnalytics isVisible={activeTab === 4} />
        </Box>
      )}

      {activeTab === 5 && (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <UserManagement isVisible={activeTab === 5} />
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
